<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar horario" : "Nuevo horario" }}
      </h1>
    </div>

    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 760px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>{{ editing ? "Datos del horario" : "Datos del nuevo horario" }}</h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section mb-0">
              <div class="row">

                <!-- Nombre -->
                <div class="col-md-8">
                  <div class="form-group">
                    <label>Nombre <span class="text-danger">*</span></label>
                    <input
                      type="text"
                      v-model="form.name"
                      autocomplete="off"
                      placeholder="Ej: Oficina estándar"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.name.$error }"
                    />
                    <FormError
                      v-if="$v.form.name.$error && !$v.form.name.required"
                      message="El nombre es requerido"
                    />
                  </div>
                </div>

                <!-- Código -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Código</label>
                    <input
                      type="text"
                      v-model="form.code"
                      autocomplete="off"
                      placeholder="Ej: HOR-01"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <!-- Días laborables -->
                <div class="col-md-12">
                  <div class="form-group">
                    <label>Días laborables <span class="text-danger">*</span></label>
                    <div class="d-flex flex-wrap gap-2">
                      <div v-for="day in workDaysOptions" :key="day.key" class="mr-3 mb-1">
                        <b-form-checkbox
                          v-model="selectedDays"
                          :value="day.key"
                        >{{ day.short }}</b-form-checkbox>
                      </div>
                    </div>
                    <FormError
                      v-if="$v.selectedDays.$error && !$v.selectedDays.required"
                      message="Debe seleccionar al menos un día"
                    />
                  </div>
                </div>

                <!-- Hora entrada -->
                <div class="col-md-3">
                  <div class="form-group">
                    <label>Hora entrada <span class="text-danger">*</span></label>
                    <input
                      type="time"
                      v-model="form.entryTime"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.entryTime.$error }"
                    />
                    <FormError
                      v-if="$v.form.entryTime.$error && !$v.form.entryTime.required"
                      message="La hora de entrada es requerida"
                    />
                  </div>
                </div>

                <!-- Hora salida -->
                <div class="col-md-3">
                  <div class="form-group">
                    <label>Hora salida <span class="text-danger">*</span></label>
                    <input
                      type="time"
                      v-model="form.exitTime"
                      class="custom-form-control"
                      :class="{ 'custom-form-control-error': $v.form.exitTime.$error }"
                    />
                    <FormError
                      v-if="$v.form.exitTime.$error && !$v.form.exitTime.required"
                      message="La hora de salida es requerida"
                    />
                  </div>
                </div>

                <!-- Inicio descanso -->
                <div class="col-md-3">
                  <div class="form-group">
                    <label>Inicio descanso</label>
                    <input
                      type="time"
                      v-model="form.lunchStart"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <!-- Fin descanso -->
                <div class="col-md-3">
                  <div class="form-group">
                    <label>Fin descanso</label>
                    <input
                      type="time"
                      v-model="form.lunchEnd"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <!-- Tolerancia -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Tolerancia entrada (min)</label>
                    <input
                      type="number"
                      v-model.number="form.toleranceMinutes"
                      min="0"
                      placeholder="0"
                      class="custom-form-control"
                    />
                  </div>
                </div>

                <!-- Horas efectivas -->
                <div class="col-md-4">
                  <div class="form-group">
                    <label>Horas efectivas / día</label>
                    <div class="input-group">
                      <input
                        type="number"
                        v-model.number="form.effectiveHours"
                        step="0.25"
                        min="0"
                        placeholder="Auto"
                        class="custom-form-control"
                      />
                      <div class="input-group-append">
                        <span class="input-group-text">h</span>
                      </div>
                    </div>
                    <small class="text-muted">Se calcula automáticamente al cambiar los horarios.</small>
                  </div>
                </div>

              </div>

              <div class="form-group-buttons text-right">
                <router-link :to="{ name: 'work-schedule-list' }" class="button button-light">
                  {{ editing ? "Volver" : "Cancelar" }}
                </router-link>
                <button class="button button-primary" type="submit">Guardar</button>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormError } from "wize-admin";
import { required } from "vuelidate/lib/validators";
import { WorkScheduleService } from "@/core/services";

const WORK_DAYS_OPTIONS = [
  { key: 1, label: 'Lunes',     short: 'Lun' },
  { key: 2, label: 'Martes',    short: 'Mar' },
  { key: 3, label: 'Miércoles', short: 'Mié' },
  { key: 4, label: 'Jueves',    short: 'Jue' },
  { key: 5, label: 'Viernes',   short: 'Vie' },
  { key: 6, label: 'Sábado',    short: 'Sáb' },
  { key: 7, label: 'Domingo',   short: 'Dom' },
];

export default {
  components: { FormError },

  data() {
    return {
      editing: false,
      workDaysOptions: WORK_DAYS_OPTIONS,
      selectedDays: [],
      form: {
        name:             null,
        code:             null,
        entryTime:        null,
        exitTime:         null,
        lunchStart:       null,
        lunchEnd:         null,
        toleranceMinutes: 0,
        effectiveHours:   null,
      },
    };
  },

  validations() {
    return {
      form: {
        name:      { required },
        entryTime: { required },
        exitTime:  { required },
      },
      selectedDays: {
        required: (val) => val && val.length > 0,
      },
    };
  },

  watch: {
    'form.entryTime':  'calcEffectiveHours',
    'form.exitTime':   'calcEffectiveHours',
    'form.lunchStart': 'calcEffectiveHours',
    'form.lunchEnd':   'calcEffectiveHours',
  },

  methods: {
    calcEffectiveHours() {
      if (!this.form.entryTime || !this.form.exitTime) return;
      const toMin = (t) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
      };
      let mins = toMin(this.form.exitTime) - toMin(this.form.entryTime);
      if (mins < 0) mins += 24 * 60; // nocturno: cruza medianoche
      if (this.form.lunchStart && this.form.lunchEnd) {
        let lunchMins = toMin(this.form.lunchEnd) - toMin(this.form.lunchStart);
        if (lunchMins < 0) lunchMins += 24 * 60;
        mins -= lunchMins;
      }
      if (mins > 0) {
        this.form.effectiveHours = parseFloat((mins / 60).toFixed(2));
      }
    },

    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          const el = document.querySelector('.form-error');
          if (el) this.$smoothScroll({ scrollTo: el, updateHistory: false, offset: -50 });
        });
        return;
      }

      try {
        this.$store.dispatch("app/loading", true);
        const data = {
          ...this.form,
          workDays: [...this.selectedDays].sort((a, b) => a - b).join(','),
        };
        if (!this.editing) {
          const resp = await WorkScheduleService.save(data);
          Alert.success("Se almacenó la información correctamente");
          this.$router.push({ name: 'work-schedule-edit', params: { id: resp.payload.id } });
          this.editing = true;
        } else {
          await WorkScheduleService.update(this.$route.params.id, data);
          Alert.success("Se almacenó la información correctamente");
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "Error al guardar");
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },

    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const payload = (await WorkScheduleService.get(id)).payload;
        this.form = {
          name:             payload.name,
          code:             payload.code,
          entryTime:        payload.entryTime,
          exitTime:         payload.exitTime,
          lunchStart:       payload.lunchStart,
          lunchEnd:         payload.lunchEnd,
          toleranceMinutes: payload.toleranceMinutes,
          effectiveHours:   payload.effectiveHours ? parseFloat(payload.effectiveHours) : null,
        };
        this.selectedDays = payload.workDaysList || [];
        this.editing = true;
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "Error al cargar el horario");
        this.$router.push({ name: 'work-schedule-list' });
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },

  async created() {
    if (this.$route.params.id) await this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped></style>
